import { TestId } from '@trello/test-ids';
import classNames from 'classnames';
import React from 'react';
import styles from './Button.less';
import Spinner from '@atlaskit/spinner';

type ForwardedComponent<T, P> = React.ComponentType<
  React.PropsWithoutRef<P> & React.RefAttributes<T>
>;

const forwardRefComponent = <T, P>(
  displayName: string,
  fn: React.RefForwardingComponent<T, P>,
): ForwardedComponent<T, P> => {
  fn.displayName = displayName;

  return React.forwardRef<T, P>(fn);
};

export enum ButtonTypes {
  DEFAULT = 'default',
  ICON = 'icon',
  PRIMARY = 'primary',
  DANGER = 'danger',
  DISABLED = 'disabled',
  LINKBUTTON = 'linkButton',
}
export enum ButtonSizes {
  DEFAULT = 'default',
  WIDE = 'wide',
  FULLWIDTH = 'fullwidth',
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  size?: ButtonSizes;
  appearance?:
    | 'default'
    | 'icon'
    | 'primary'
    | 'danger'
    | 'disabled'
    | 'linkButton';
  buttonType?: ButtonTypes;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  testId?: TestId;
  isLoading?: boolean;
  shouldFitContainer?: boolean;
  isDisabled?: boolean;
}

export const Button = forwardRefComponent<HTMLButtonElement, ButtonProps>(
  'Button',
  (
    {
      children,
      onClick,
      className,
      appearance,
      buttonType,
      size,
      testId,
      isLoading,
      isDisabled,
      shouldFitContainer,
      ...buttonAttrs
    },
    ref,
  ) => {
    const { disabled } = buttonAttrs;

    if (shouldFitContainer) {
      size = ButtonSizes.FULLWIDTH;
    }

    if (isDisabled) {
      appearance = ButtonTypes.DISABLED;
    }

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={classNames(
          styles.button,
          className,
          styles[appearance || buttonType || ButtonTypes.DEFAULT],
          styles[size || ButtonSizes.DEFAULT],
          { [styles[ButtonTypes.DISABLED]]: disabled },
        )}
        onClick={onClick}
        data-test-id={testId}
        type={
          'button' // automatically prevents default on forms
        }
        {...buttonAttrs}
      >
        {isLoading ? <Spinner /> : children}
      </button>
    );
  },
);

export const LinkButton = forwardRefComponent<HTMLButtonElement, ButtonProps>(
  'LinkButton',
  ({ children, onClick, className, ...buttonAttrs }, ref) => {
    return (
      <Button
        ref={ref}
        buttonType={ButtonTypes.LINKBUTTON}
        onClick={onClick}
        className={className}
        {...buttonAttrs}
      >
        {children}
      </Button>
    );
  },
);

export interface IconButtonProps extends ButtonProps {
  icon: JSX.Element;
}

export const IconButton = forwardRefComponent<
  HTMLButtonElement,
  IconButtonProps
>(
  'IconButton',
  ({ children, icon, onClick, size, className, ...buttonAttrs }, ref) => {
    const {
      color,
      size: iconSize,
      dangerous_className: iconClass,
    } = icon.props;
    const iconClassName = classNames(
      {
        [styles.largeIcon]: iconSize === 'large',
        [styles.smallIcon]: iconSize === 'small',
        [styles['button-icon-dark']]: color === 'dark',
      },
      iconClass,
    );

    return (
      <Button
        ref={ref}
        size={size}
        onClick={onClick}
        className={classNames(styles.iconButton, className, {
          [styles.iconButtonSmall]: iconSize === 'small',
          [styles.iconButtonLarge]: iconSize === 'large',
        })}
        {...buttonAttrs}
      >
        {React.cloneElement(icon, {
          dangerous_className: iconClassName,
          size: iconSize,
          color: color,
        })}
        {children && (
          <span className={styles.iconButtonContents}>{children}</span>
        )}
      </Button>
    );
  },
);

export const PrimaryButton = forwardRefComponent<
  HTMLButtonElement,
  ButtonProps
>('PrimaryButton', ({ children, onClick, size, ...buttonAttrs }, ref) => {
  return (
    <Button
      ref={ref}
      buttonType={ButtonTypes.PRIMARY}
      size={size}
      onClick={onClick}
      {...buttonAttrs}
    >
      {children}
    </Button>
  );
});

export const DangerButton = forwardRefComponent<HTMLButtonElement, ButtonProps>(
  'DangerButton',
  ({ children, onClick, size, ...buttonAttrs }, ref) => {
    return (
      <Button
        ref={ref}
        buttonType={ButtonTypes.DANGER}
        size={size}
        onClick={onClick}
        {...buttonAttrs}
      >
        {children}
      </Button>
    );
  },
);

interface LinkProps {
  children: React.ReactNode;
  link: string;
  isPrimary: boolean;
  onClick?: React.EventHandler<React.MouseEvent<HTMLElement>>;
  openInNewTab?: boolean;
  className?: string;
}

export const ButtonLink = ({
  children,
  link,
  isPrimary,
  onClick,
  openInNewTab,
  className,
}: LinkProps) => {
  const buttonStyle: string = isPrimary
    ? styles.primaryButtonLink
    : styles.defaultButtonLink;
  let linkTarget: string | undefined = undefined;
  let rel: string | undefined = undefined;

  if (openInNewTab) {
    linkTarget = '_blank';
    rel = 'noreferrer';
  }

  return (
    <a
      href={link}
      className={classNames(buttonStyle, className)}
      onClick={onClick}
      target={linkTarget}
      rel={rel}
    >
      <span>{children}</span>
    </a>
  );
};
